import { EmailField, StateSelectField } from "@tellescope/forms";
import { LabeledField, StateCredentialInfo } from "@tellescope/types-models";
import {
  Button,
  Checkbox,
  Combobox,
  DatePicker,
  Input,
  Label,
  MultiSelect,
  NumberInput,
  PageTabsContent,
  PhoneInput,
  Separator,
  Textarea,
} from "@tellescope/ui";
import { X } from "lucide-react";
import { Fragment } from "react";
import { EditUserForm } from "../useEditUserForm";
import { useProfileTabContent } from "./useProfileTabContent";

export interface ProfileTabContentProps {
  userId?: string;
  form: EditUserForm;
  credentialedStates: StateCredentialInfo[];
  onAddCredentialedState: () => void;
  onUpdateCredentialedState: <T extends keyof StateCredentialInfo>(
    stateOrIndex: string | number,
    key: T,
    value: StateCredentialInfo[T]
  ) => void;
  onDeleteCredentialedState: (s: string | number) => void;
  templateFields: LabeledField[];
  onAddTemplateField: () => void;
  onUpdateTemplateField: <T extends keyof LabeledField>(i: number, key: T, value: LabeledField[T]) => void;
  onRemoveTemplateField: (i: number) => void;
}

export const ProfileTabContent = (props: ProfileTabContentProps) => {
  const {
    userId,
    form,
    credentialedStates,
    onAddCredentialedState,
    onUpdateCredentialedState,
    onDeleteCredentialedState,
    templateFields,
    onAddTemplateField,
    onUpdateTemplateField,
    onRemoveTemplateField,
  } = props;
  const {
    t,
    user,
    hasScriptSure,
    hasConnectedCanvas,
    hasConnectedPaubox,
    locationOptions,
    selectedLocationOption,
    canvasOptions,
    selectedCanvasOption,
    linkScriptSurePrescriberId,
    scriptsureLinkIsPending,
    suffixOptions,
    templateFieldKeyOptions,
    statesWithCredentials,
  } = useProfileTabContent({ userId, form, credentialedStates, templateFields });

  return (
    <PageTabsContent
      value="profile"
      className="px-6 py-4 space-y-4 overflow-y-auto"
      style={{
        maxHeight: "min(80vh, 900px)",
      }}
    >
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="email">{t("Email")}</Label>
          <EmailField
            id="email"
            value={form.values.email}
            onChange={(newVal) => form.setFieldValue("email", newVal ?? "")}
            disabled={!!userId}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="fname">{t("First Name")}</Label>
          <Input id="fname" {...form.getFieldProps("fname")} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lname">{t("Last Name")}</Label>
          <Input id="lname" {...form.getFieldProps("lname")} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="internalDisplayName">{t("Display Name (team facing)")}</Label>
          <Input id="displayName" {...form.getFieldProps("internalDisplayName")} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="displayName">{t("Display Name (patient facing)")}</Label>
          <Input id="displayName" {...form.getFieldProps("displayName")} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="defaultLocationId">{t("Default Location")}</Label>
          <Combobox
            id="defaultLocationId"
            value={selectedLocationOption}
            onChange={(option) => form.setFieldValue("defaultLocationId", option?.value ?? "")}
            options={locationOptions}
            placeholder={t("No default location set")}
            searchPlaceholder={t("Search locations...")}
            emptyText={t("No locations found")}
            clearable
          />
        </div>
        {hasScriptSure && (
          <div className="space-y-2">
            <Label htmlFor="scriptSure">{t("ScriptSure Prescriber ID")}</Label>
            {user?.scriptSurePrescriberId ? (
              <p className="text-sm text-muted-foreground">{user.scriptSurePrescriberId}</p>
            ) : (
              <Button
                variant="outline"
                disabled={!user?.email || !userId}
                loading={scriptsureLinkIsPending}
                onClick={() => linkScriptSurePrescriberId(userId || "")}
              >
                {t("Link ScriptSure")}
              </Button>
            )}
          </div>
        )}
        {hasConnectedCanvas && (
          <>
            <div className="space-y-2">
              <Label htmlFor="canvasId">{t("Canvas User ID")}</Label>
              <Combobox
                id="canvasId"
                value={selectedCanvasOption}
                onChange={(option) => form.setFieldValue("canvasId", option?.value ?? "")}
                options={canvasOptions}
                customFilter={(searchTerm, providerOptions) => {
                  const lowerSearch = searchTerm.toLowerCase();
                  const matches = providerOptions.filter(
                    (option) =>
                      option.label.toLowerCase().includes(lowerSearch) ||
                      option.value.toLowerCase().includes(lowerSearch)
                  );
                  if (!searchTerm || providerOptions.some((option) => option.value === searchTerm)) {
                    return matches;
                  }
                  return [...matches, { value: searchTerm, label: t('Use ID "{{id}}"', { id: searchTerm }) }];
                }}
                placeholder={t("No Canvas user linked")}
                searchPlaceholder={t("Search providers or enter an ID...")}
                emptyText={t("No Canvas providers found")}
                clearable
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="careTeamRole">{t("Canvas Care Team Role")}</Label>
              <Input id="careTeamRole" {...form.getFieldProps("careTeamRole")} />
            </div>
          </>
        )}
        {hasConnectedPaubox && (
          <div className="space-y-2">
            <Label htmlFor="pauboxEmail">{t("Paubox Email Address")}</Label>
            <EmailField
              id="pauboxEmail"
              value={form.values.pauboxEmail}
              onChange={(newVal) => form.setFieldValue("pauboxEmail", newVal ?? "")}
            />
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="phoneNumber">{t("Phone Number")}</Label>
          <PhoneInput id="phoneNumber" {...form.getCustomFieldProps("phone")} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="url">{t("URL (include in templates as {{sender.url}})")}</Label>
          <PhoneInput id="url" {...form.getCustomFieldProps("url")} />
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="hideFromCalendarView"
            checked={form.values.hideFromCalendarView}
            onCheckedChange={(checked) => form.setFieldValue("hideFromCalendarView", !!checked)}
          />
          <Label htmlFor="hideFromCalendarView">{t("Hide from Calendar Dropdown")}</Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="disableTicketAutoAssignment"
            onCheckedChange={(checked) => form.setFieldValue("disableTicketAutoAssignment", !!checked)}
            checked={form.values.disableTicketAutoAssignment}
          />
          <Label htmlFor="disableTicketAutoAssignment">{t("Disable Ticket Auto-Assignment")}</Label>
        </div>
        <div className="space-y-2">
          <Label htmlFor="suffixes">{t("Suffixes (MD, PA-C, RN)")}</Label>
          <MultiSelect
            id="suffixes"
            options={suffixOptions}
            {...form.getCustomFieldProps("suffixes")}
            placeholder={t("No suffixes set")}
            searchPlaceholder={t("Search suffixes or enter a value...")}
            emptyText={t("No suffixes found")}
            creator={{
              render: (inputValue) => t('Add "{{value}}"', { value: inputValue }),
              isValidNewOption: (inputValue, selectedValues) =>
                !!inputValue.trim() && !selectedValues.includes(inputValue.trim()),
              transform: (inputValue) => inputValue.trim(),
              onCreate: (suffix) => {
                form.setFieldValue("suffixes", [...form.values.suffixes, suffix.value]);
              },
            }}
            showAsBadges={false}
            className="h-8 w-full"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ticketAssignmentPriority">{t("Ticket Assignment Priority")}</Label>
          <NumberInput
            id="ticketAssignmentPriority"
            {...form.getCustomFieldProps("ticketAssignmentPriority")}
            min={1}
            max={10}
          />
        </div>
        <div className="space-y-2 col-span-2">
          <Label htmlFor="bio">{t("Bio")}</Label>
          <Textarea id="bio" {...form.getFieldProps("bio")} />
        </div>
        <div className="space-y-2 col-span-2">
          <Label htmlFor="emailSignature">{t("Email Signature")}</Label>
          <Textarea id="emailSignature" {...form.getFieldProps("emailSignature")} />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="space-y-2">
          <Label htmlFor="NPI">{t("NPI")}</Label>
          <Input id="NPI" {...form.getFieldProps("NPI")} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="DEA">{t("DEA")}</Label>
          <Input id="DEA" {...form.getFieldProps("DEA")} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="TIN">{t("TIN")}</Label>
          <Input id="TIN" {...form.getFieldProps("TIN")} />
        </div>
      </div>
      <Separator />
      <div className="grid grid-cols-[1fr_1fr_1fr_24px] gap-2">
        <Label className="col-span-4 text-lg">{t("Credentialed States")}</Label>
        {credentialedStates?.map(({ state, licenseId, expiresAt }, i) => {
          return (
            <Fragment key={state}>
              <div className="space-y-2">
                <StateSelectField
                  value={state}
                  onValueChange={(newState) => onUpdateCredentialedState(state, "state", newState)}
                  statesToExclude={statesWithCredentials.map(({ value }) => value)}
                />
              </div>
              <div className="space-y-2">
                <Input
                  value={licenseId}
                  onChange={(e) => onUpdateCredentialedState(state, "licenseId", e.target.value)}
                  placeholder={t("License number")}
                />
              </div>
              <div className="space-y-2">
                <DatePicker
                  value={expiresAt}
                  onChange={(newDate) => onUpdateCredentialedState(state, "expiresAt", newDate)}
                  label=""
                  placeholder={t("Expiration Date")}
                />
              </div>
              <Button variant="ghost" onClick={() => onDeleteCredentialedState(state || i)}>
                <X />
              </Button>
            </Fragment>
          );
        })}
        <Button variant="outline" size="sm" onClick={onAddCredentialedState}>
          {t("Add Credentialed State")}
        </Button>
      </div>
      <Separator />
      <div className="grid grid-cols-[1fr_1fr_24px] gap-2">
        <Label className="col-span-3 text-lg">{t("Custom Fields for Templates")}</Label>
        {templateFields.map((field, i) => (
          <Fragment key={field.field}>
            <div className="space-y-2">
              <Label className="text-muted-foreground">{t("Field")}</Label>
              <Combobox
                value={{
                  value: field.field,
                  label: field.field,
                }}
                onChange={(option) => onUpdateTemplateField(i, "field", option?.value ?? "")}
                options={templateFieldKeyOptions}
                customFilter={(searchTerm, fieldOptions) => {
                  const lowerSearch = searchTerm.toLowerCase();
                  const matches = fieldOptions.filter((option) => option.label.toLowerCase().includes(lowerSearch));
                  if (!searchTerm || fieldOptions.some((option) => option.value === searchTerm)) {
                    return matches;
                  }
                  return [...matches, { value: searchTerm, label: t('Use "{{value}}"', { value: searchTerm }) }];
                }}
                placeholder={t("Field")}
                searchPlaceholder={t("Search or enter a value...")}
                emptyText={t("No matching fields")}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground">{t("Value")}</Label>
              <Input value={field.value} onChange={(e) => onUpdateTemplateField(i, "value", e.target.value)} />
            </div>
            <div className="flex flex-col justify-end">
              <Button size="icon" variant="ghost" onClick={() => onRemoveTemplateField(i)}>
                <X />
              </Button>
            </div>
          </Fragment>
        ))}
        <div className="col-span-3 flex">
          <Button variant="outline" onClick={onAddTemplateField}>
            {t("Add Field")}
          </Button>
        </div>
      </div>
    </PageTabsContent>
  );
};
